---
id: go-shared-callers
title: Shared HTTP callers
owner: Backend Team
status: Active
lastReviewed: 2026-01-30
visibility: internal
tags:
  - callers
  - http
  - shared
---

## Product Overview

### Who it serves

This shared package provides reusable HTTP client helpers for Go microservices that need to call other Rezi services consistently.

### Primary workflow

Service code constructs a caller for a target service, builds a request with the helper builders, and executes the call while parsing the response into typed models.

### Business rules

Calls are routed to dev or prod service URLs based on the caller configuration, and responses are normalized to a consistent error shape.

### Outcomes

Teams can issue cross-service requests without duplicating URL routing, request building, and response parsing logic.

### Out of scope

Service-specific business rules remain in each microservice and are not enforced by this package.

## Technical Overview

### Architecture flow

`Caller` builds HTTP requests against a base URL, executes them, and parses JSON, protobuf, or binary responses through a shared response pipeline.

### Entry points

The main entry points are `NewCaller` and `NewCustomCaller` in `apps/micro-services/go-shared/callers/caller.go`, plus the `NewRequest` and `NewProtoRequest` builders used by callers like `DBApi` and `User`.

### Key modules

Service URL routing lives in the `serviceURLMap` constant map, while request/response handling is implemented in `caller.go`, and typed clients are defined in `dbapi.go` and `user.go`.

### Data sources and sinks

This package sends HTTP requests to internal services such as db-api, user-service, resume-service, and others defined in the service URL map.

### Integrations and side effects

Response parsing supports JSON payloads, protobuf JSON bodies, and binary content like PDFs, returning normalized `errs` when requests fail.

### Constraints and risks

Incorrect base URL mappings or missing service URLs will result in empty callers that cannot reach targets, and downstream error handling relies on consistent response shapes.

## Changelog

- 2026-01-30: Initial documentation.
